import * as i0 from '@angular/core';
import { inject, Injectable, ElementRef, Renderer2, Directive, Output, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractTuiEditor, TIPTAP_EDITOR, TUI_EDITOR_OPTIONS, EDITOR_BLANK_COLOR, INITIALIZATION_TIPTAP_CONTAINER } from '@taiga-ui/editor/common';
import { startWith, map, distinctUntilChanged } from 'rxjs';
import { tuiPx } from '@taiga-ui/cdk';
import { tuiParseStyle, tuiGetMarkRange } from '@taiga-ui/editor/utils';
import { EditorState } from '@tiptap/pm/state';
function tuiIsEmptyParagraph(json) {
  return Array.isArray(json) && json.length === 1 && json[0]?.type === 'paragraph' && !json[0]?.hasOwnProperty('content');
}
let TuiTiptapEditorService = /*#__PURE__*/(() => {
  class TuiTiptapEditorService extends AbstractTuiEditor {
    constructor() {
      super();
      this.editorRef = inject(TIPTAP_EDITOR);
      this.options = inject(TUI_EDITOR_OPTIONS);
      this.selectionSnapshot = null;
      this.editorRef.subscribe(editor => {
        if (!editor) {
          return;
        }
        this.editor = editor;
        const update = () => {
          const content = editor.getHTML();
          const json = editor.getJSON().content;
          const value = tuiIsEmptyParagraph(json) ? '' : content;
          this.valueChange$.next(value);
          this.stateChange$.next();
        };
        editor.on('transaction', update.bind(this));
        editor.on('update', update.bind(this));
      });
    }
    get isFocused() {
      return this.editor?.isFocused ?? false;
    }
    get html() {
      return this.editor?.getHTML() ?? '';
    }
    get editable() {
      return this.editor?.isEditable ?? false;
    }
    set editable(editable) {
      this.editor?.setEditable(editable, false);
    }
    get state() {
      return this.editor?.state ?? null;
    }
    getOriginTiptapEditor() {
      return this.editor ?? null;
    }
    undoDisabled() {
      return !this.editor?.can().undo();
    }
    redoDisabled() {
      return !this.editor?.can().redo();
    }
    getFontColor() {
      return this.editor?.getAttributes('textStyle').fontColor || EDITOR_BLANK_COLOR;
    }
    getFontSize() {
      return parseInt(this.editor?.getAttributes('textStyle').fontSize, 10);
    }
    setFontSize(size) {
      this.editor?.chain().setMark('textStyle', {
        fontSize: tuiPx(size)
      }).run();
    }
    getBackgroundColor() {
      return this.editor?.getAttributes('textStyle').backgroundColor || 'transparent';
    }
    getCellColor() {
      return this.editor?.getAttributes('tableCell').background || this.editor?.getAttributes('tableHeader').background || '';
    }
    getGroupColor() {
      if (this.editor?.isActive('group')) {
        const style = this.editor.getAttributes('group')?.style ?? '';
        const styles = tuiParseStyle(style);
        return styles['background-color'] ?? styles['background'] ?? '';
      }
      return '';
    }
    onAlign(align) {
      this.editor?.chain().focus().setTextAlign(align).run();
    }
    setImage(src) {
      this.editor?.chain().focus().command(({
        commands,
        state
      }) => {
        const setImage = commands.setEditableImage ?? commands.setImage;
        if (setImage) {
          const anchor = state.selection.anchor;
          setImage({
            src
          });
          commands.setTextSelection(anchor);
          return true;
        }
        return false;
      }).run();
    }
    undo() {
      this.editor?.chain().undo().run();
    }
    redo() {
      this.editor?.chain().redo().run();
    }
    setHorizontalRule() {
      this.editor?.chain().focus().setHorizontalRule().run();
    }
    removeFormat() {
      this.editor?.commands.unsetAllMarks();
      this.editor?.commands.clearNodes();
    }
    setFontColor(color) {
      this.editor?.chain().focus().setFontColor(color).run();
    }
    setBackgroundColor(color) {
      this.editor?.chain().focus().setBackgroundColor(color).run();
    }
    toggleUnderline() {
      this.editor?.chain().focus().toggleUnderline().run();
    }
    toggleStrike() {
      this.editor?.chain().focus().toggleStrike().run();
    }
    toggleOrderedList() {
      this.editor?.chain().focus().toggleOrderedList().run();
    }
    toggleUnorderedList() {
      this.editor?.chain().focus().toggleBulletList().run();
    }
    togglePre() {
      this.editor?.chain().focus().toggleCodeBlock().run();
    }
    toggleTaskList() {
      this.editor?.commands.toggleTaskList();
    }
    sinkListItem() {
      const type = this.editor?.isActive('taskList') ? 'taskItem' : 'listItem';
      this.editor?.chain().focus().sinkListItem(type).run();
    }
    liftListItem() {
      const type = this.editor?.isActive('taskList') ? 'taskItem' : 'listItem';
      this.editor?.chain().focus().liftListItem(type).run();
    }
    isActive(nameOrAttributes) {
      return this.editor?.isActive(nameOrAttributes) ?? false;
    }
    isActive$(nameOrAttributes) {
      return this.stateChange$.pipe(startWith(null), map(() => this.isActive(nameOrAttributes)), distinctUntilChanged());
    }
    toggleBold() {
      this.editor?.chain().focus().toggleBold().run();
    }
    toggleCode() {
      this.editor?.chain().focus().toggleCode().run();
    }
    toggleItalic() {
      this.editor?.chain().focus().toggleItalic().run();
    }
    toggleBlockquote() {
      this.editor?.chain().focus().toggleBlockquote().run();
    }
    toggleSubscript() {
      this.editor?.chain().focus().toggleSubscript().run();
    }
    toggleSuperscript() {
      this.editor?.chain().focus().toggleSuperscript().run();
    }
    toggleCodeBlock() {
      this.editor?.chain().focus().toggleCodeBlock().run();
    }
    insertTable(cols, rows) {
      this.editor?.chain().focus().insertTable({
        cols,
        rows
      }).run();
    }
    addColumnAfter() {
      this.editor?.chain().focus().addColumnAfter().run();
    }
    addColumnBefore() {
      this.editor?.chain().focus().addColumnBefore().run();
    }
    addRowAfter() {
      this.editor?.chain().focus().addRowAfter().run();
    }
    addRowBefore() {
      this.editor?.chain().focus().addRowBefore().run();
    }
    deleteColumn() {
      this.editor?.chain().focus().deleteColumn().run();
    }
    deleteRow() {
      this.editor?.chain().focus().deleteRow().run();
    }
    mergeCells() {
      this.editor?.chain().focus().mergeCells().run();
    }
    splitCell() {
      this.editor?.chain().focus().splitCell().run();
    }
    canMergeCells() {
      return this.editor?.can().mergeCells() ?? false;
    }
    canSplitCells() {
      return this.editor?.can().splitCell() ?? false;
    }
    setHeading(level) {
      this.editor?.chain().focus().setHeading({
        level
      }).run();
    }
    setParagraph(options) {
      this.editor?.chain().focus().setParagraph().run();
      if (options) {
        this.editor?.chain().setMark('textStyle', options).run();
      }
    }
    setHardBreak() {
      this.editor?.chain().setHardBreak().run();
    }
    setTextSelection(value) {
      this.editor?.commands.setTextSelection(value);
    }
    toggleLink(href) {
      this.editor?.chain().focus().toggleLink({
        href
      }).run();
    }
    setLink(href) {
      this.editor?.chain().focus().setLink({
        href
      }).run();
    }
    unsetLink() {
      this.editor?.chain().focus().unsetLink().run();
    }
    focus() {
      this.editor?.chain().focus().run();
    }
    setValue(value) {
      if (value === this.html || value === '' && this.html === '<p></p>') {
        return;
      }
      this.editor?.commands.setContent(value, false, this.options.parseOptions);
      this.editor?.view.updateState(EditorState.create({
        schema: this.editor.state.schema,
        doc: this.editor.state.doc,
        selection: this.editor.state.selection,
        storedMarks: this.editor.state.storedMarks,
        plugins: this.editor.state.plugins
      }));
    }
    destroy() {
      this.editor?.destroy();
    }
    setCellColor(color) {
      this.editor?.chain().focus().setCellBackground(color).run();
    }
    selectClosest() {
      const pos = this.editor?.state.selection.anchor;
      const range = tuiGetMarkRange(this.editor?.state.tr.doc.resolve(pos ?? 0), this.editor?.schema.marks.link);
      if (range) {
        this.editor?.chain().setTextSelection(range).run();
      }
    }
    enter() {
      this.editor?.commands.enter();
    }
    setDetails() {
      this.editor?.commands.setDetails();
    }
    removeDetails() {
      this.editor?.commands.removeDetails();
    }
    setGroup() {
      this.editor?.commands.setGroup();
    }
    removeGroup() {
      this.editor?.commands.removeGroup();
    }
    setGroupHilite(color) {
      this.editor?.commands.setGroupHilite(color);
    }
    setAnchor(anchor) {
      this.editor?.commands.setAnchor(anchor.replace('#', ''));
    }
    removeAnchor() {
      this.editor?.commands.removeAnchor();
    }
    setFileLink(preview) {
      this.editor?.commands.setFileLink(preview);
    }
    setYoutubeVideo(options) {
      this.editor?.commands.setYoutubeVideo(options);
    }
    setIframe(options) {
      this.editor?.commands.setIframe(options);
    }
    removeEmptyTextStyle() {
      this.editor?.commands.removeEmptyTextStyle();
    }
    toggleMark(typeOrName, attributes, options) {
      this.editor?.commands.toggleMark(typeOrName, attributes, options);
    }
    getHTML() {
      return this.getOriginTiptapEditor()?.getHTML() ?? '';
    }
    takeSelectionSnapshot() {
      this.selectionSnapshot = this.editor?.state.selection.toJSON() ?? null;
    }
    getSelectionSnapshot() {
      return this.selectionSnapshot;
    }
    static {
      this.ɵfac = function TuiTiptapEditorService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiTiptapEditorService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TuiTiptapEditorService,
        factory: TuiTiptapEditorService.ɵfac
      });
    }
  }
  return TuiTiptapEditorService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiTiptapEditor = /*#__PURE__*/(() => {
  class TuiTiptapEditor {
    constructor() {
      this.el = inject(ElementRef);
      this.renderer = inject(Renderer2);
      this.editor = inject(TuiTiptapEditorService);
      this.editorContainer = inject(INITIALIZATION_TIPTAP_CONTAINER);
      this.$ = inject(TIPTAP_EDITOR).pipe(takeUntilDestroyed()).subscribe(() => this.renderer.appendChild(this.el.nativeElement, this.editorContainer));
      this.valueChange = this.editor.valueChange$.pipe(distinctUntilChanged());
      this.stateChange = this.editor.stateChange$;
    }
    set value(value) {
      this.editor.setValue(value);
    }
    set editable(editable) {
      this.editor.editable = editable;
    }
    static {
      this.ɵfac = function TuiTiptapEditor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiTiptapEditor)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiTiptapEditor,
        selectors: [["", "tuiTiptapEditor", ""]],
        inputs: {
          value: "value",
          editable: "editable"
        },
        outputs: {
          valueChange: "valueChange",
          stateChange: "stateChange"
        }
      });
    }
  }
  return TuiTiptapEditor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TuiTiptapEditor, TuiTiptapEditorService, tuiIsEmptyParagraph };
